import { API_BASE_URL } from '@/config'
import { translateBackendUnavailableReason } from '@/helper/apiError.helpers'
import { useSystemStore } from '@/stores/system.store'

function isBackendDownStatus(status: number) {
  // consider 5xx as backend issues; you can narrow to 502/503/504 if you prefer
  return status >= 500
}

function markBackendUp() {
  useSystemStore.getState().setBackendUp()
}

function markBackendDown(reason?: string) {
  console.error('[API] Backend unavailable:', reason)
  useSystemStore.getState().setBackendDown(translateBackendUnavailableReason(reason))
}

let refreshPromise: Promise<boolean> | null = null

export type ApiRequestInit = RequestInit & {
  /**
   * A failing non-critical request (a preview, a hint, a badge count) must not flip the whole
   * app into the "Verbindung nicht verfügbar" overlay. Its errors are still thrown to the
   * caller; only the global backend-down marking is skipped for 5xx and network failures.
   */
  nonCritical?: boolean
}

// ---- GLOBAL GET DEDUPE + CACHE ----

type CacheEntry = { expiresAt: number; value: unknown }

// key: "GET /orders?year=2026"
const inFlightGET = new Map<string, Promise<unknown>>()
const cacheGET = new Map<string, CacheEntry>()

const DEFAULT_GET_TTL_MS = 30_000 // tune (e.g. 30s)

function ttlFor(path: string) {
  if (path.startsWith('/orders?')) return 2 * 60_000 // 2 min
  if (path.startsWith('/settings/banks/')) return 10 * 60_000 // 10 min
  return DEFAULT_GET_TTL_MS
}

function canonicalizePath(path: string) {
  const [p, qs] = path.split('?')
  if (!qs) return path

  const sp = new URLSearchParams(qs)

  // sort keys + values
  const entries = Array.from(sp.entries()).sort(([aK, aV], [bK, bV]) => {
    if (aK !== bK) return aK.localeCompare(bK)
    return aV.localeCompare(bV)
  })

  const out = new URLSearchParams()
  for (const [k, v] of entries) out.append(k, v)

  const q = out.toString()
  return q ? `${p}?${q}` : p
}

function getMethod(init?: RequestInit) {
  return (init?.method || 'GET').toUpperCase()
}

function makeCacheKey(path: string, init?: RequestInit) {
  const method = getMethod(init)
  const canonicalPath = method === 'GET' ? canonicalizePath(path) : path
  return `${method} ${canonicalPath}`
}

function isCacheDisabled(init?: RequestInit) {
  const h = init?.headers as any
  return Boolean(h?.['x-disable-cache'] || h?.['X-Disable-Cache'])
}

export function invalidateApiCache(prefix?: string) {
  if (!prefix) {
    cacheGET.clear()
    return
  }
  for (const k of cacheGET.keys()) {
    if (k.includes(prefix)) cacheGET.delete(k)
  }
}

function storeCsrfFromResponse(res: Response) {
  const csrf = res.headers.get('x-csrf-token')
  if (csrf) {
    localStorage.setItem('CSRF_TOKEN', csrf)
  }
}

async function safeJson(res: Response) {
  try {
    return await res.json()
  } catch {
    return {}
  }
}
async function refreshSession(): Promise<boolean> {
  const csrf = localStorage.getItem('CSRF_TOKEN') || ''

  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf,
      },
    })

    storeCsrfFromResponse(res)

    if (res.ok || res.status < 500) {
      markBackendUp()
    } else if (isBackendDownStatus(res.status)) {
      markBackendDown(`HTTP ${res.status}`)
    }

    return res.ok
  } catch (e: any) {
    markBackendDown(e?.message || 'Network error')
    return false
  }
}

async function sendRequest(path: string, apiInit: ApiRequestInit): Promise<Response> {
  const csrf = localStorage.getItem('CSRF_TOKEN') || ''
  const { nonCritical, ...init } = apiInit

  const finalInit: RequestInit = {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
      ...(init.headers || {}),
    },
  }

  try {
    const res = await fetch(`${API_BASE_URL}${path}`, finalInit)

    // If we reached backend and got any response => backend is reachable
    if (res.ok || res.status < 500) {
      markBackendUp()
    } else if (isBackendDownStatus(res.status) && !nonCritical) {
      markBackendDown(`HTTP ${res.status}`)
    }

    return res
  } catch (e: any) {
    // Network/CORS/DNS/connection => fetch throws
    if (!nonCritical) markBackendDown(e?.message || 'Network error')
    throw e
  }
}

export async function API<R>(
  path: string,
  init: ApiRequestInit = {},
  errorMsgFallback?: string,
  errorStatusFallback?: string
): Promise<R> {
  const method = getMethod(init)

  // ---- GET CACHE (fast return) ----
  if (method === 'GET' && !isCacheDisabled(init)) {
    const key = makeCacheKey(path, init)
    const hit = cacheGET.get(key)
    if (hit && hit.expiresAt > Date.now()) {
      return hit.value as R
    }

    // ---- GET IN-FLIGHT DEDUPE (share ongoing request) ----
    const existing = inFlightGET.get(key)
    if (existing) {
      return (await existing) as R
    }

    const p = (async () => {
      try {
        // run the ORIGINAL logic (copied from below)
        let res = await sendRequest(path, init)
        storeCsrfFromResponse(res)

        if (res.status !== 401) {
          const data = await handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
          cacheGET.set(key, { value: data, expiresAt: Date.now() + DEFAULT_GET_TTL_MS })
          return data
        }

        console.warn('[API] 401 - attempting session refresh')

        if (!refreshPromise) {
          refreshPromise = refreshSession().finally(() => {
            refreshPromise = null
          })
        }

        const refreshed = await refreshPromise

        if (!refreshed) {
          throw new Error('UNAUTHENTICATED')
        }

        console.warn('[API] Refresh OK, retrying original request…')
        res = await sendRequest(path, init)
        storeCsrfFromResponse(res)

        if (res.status === 401) {
          throw new Error('UNAUTHENTICATED')
        }

        const data = await handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
        cacheGET.set(key, { value: data, expiresAt: Date.now() + DEFAULT_GET_TTL_MS })
        return data
      } finally {
        inFlightGET.delete(key)
      }
    })()

    inFlightGET.set(key, p as Promise<unknown>)
    return (await p) as R
  }

  if (method !== 'GET') {
    cacheGET.clear() // simplest and safest
  }
  // ---- non-GET: keep original behavior (no caching/dedupe) ----
  let res = await sendRequest(path, init)

  storeCsrfFromResponse(res)

  if (res.status !== 401) {
    return handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
  }

  console.warn('[API] 401 - attempting session refresh')

  if (!refreshPromise) {
    refreshPromise = refreshSession().finally(() => {
      refreshPromise = null
    })
  }

  const refreshed = await refreshPromise

  if (!refreshed) {
    throw new Error('UNAUTHENTICATED')
  }

  console.warn('[API] Refresh OK, retrying original request…')
  res = await sendRequest(path, init)

  storeCsrfFromResponse(res)

  if (res.status === 401) {
    throw new Error('UNAUTHENTICATED')
  }

  return handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
}
export async function API_PUBLIC<R>(
  path: string,
  init: RequestInit = {},
  errorMsg?: string,
  errorCode?: string
): Promise<R> {
  let res: Response

  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    })
  } catch (e: any) {
    markBackendDown(e?.message || 'Network error')
    throw e
  }

  storeCsrfFromResponse(res)

  if (res.ok || res.status < 500) {
    markBackendUp()
  } else if (isBackendDownStatus(res.status)) {
    markBackendDown(`HTTP ${res.status}`)
  }

  const data = await res.json().catch(() => ({}))

  if (!res.ok) {
    const err: any = new Error(data?.message || errorMsg)
    err.code = data?.code || errorCode
    err.errors = data?.errors
    err.data = data
    err.status = res.status
    throw err
  }

  return data
}

async function handleResponse<R>(
  res: Response,
  errorMsg?: string,
  errorCode?: string
): Promise<R> {
  const data = await safeJson(res)

  if (!res.ok) {
    const err: any = new Error(data?.message || errorMsg)
    err.code = data?.code || errorCode
    err.errors = data?.errors
    err.data = data
    err.status = res.status
    throw err
  }

  return data
}
export type BlobResponse = { blob: Blob; filename: string | null }

export async function API_BLOB(
  endpoint: string,
  options: RequestInit = {},
  errorMessage = 'Download fehlgeschlagen',
  errorCode?: string
): Promise<BlobResponse> {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/octet-stream',
        ...options.headers,
      },
      ...options,
    })

    if (res.ok || res.status < 500) {
      markBackendUp()
    } else if (isBackendDownStatus(res.status)) {
      markBackendDown(`HTTP ${res.status}`)
    }

    if (!res.ok) {
      const backendMessage = await res.text()
      throw { status: res.status, backendMessage, code: errorCode }
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = res.headers.get('Content-Disposition')
    let filename: string | null = null
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
      if (match) {
        filename = match[1]
      }
    }

    const blob = await res.blob()
    return { blob, filename }
  } catch (e: any) {
    // if fetch threw (network)
    if (e?.name === 'TypeError' || !e?.status) {
      markBackendDown(e?.message || 'Network error')
    }
    console.error(errorCode || 'API_BLOB_ERROR', e)
    throw e
  }
}
